/**
 * Normalizes merchant-entered menu titles for deterministic matching.
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeMenuTitle(value) {
  return String(value ?? '').trim().toLocaleLowerCase();
}

/**
 * Builds a title registry while preserving the first configured match.
 * @param {Iterable<HTMLElement>} elements
 * @returns {Map<string, HTMLElement>}
 */
export function buildFirstMatchMap(elements) {
  const registry = new Map();

  for (const element of elements) {
    const key = normalizeMenuTitle(element?.dataset?.menuTitle);
    if (key && !registry.has(key)) registry.set(key, element);
  }

  return registry;
}

/**
 * Activates one tab and its corresponding panel (PC exclusive mode).
 * @param {Iterable<HTMLElement>} tabs
 * @param {Iterable<HTMLElement>} panels
 * @param {string} selectedKey
 * @returns {boolean}
 */
export function updateTabSelection(tabs, panels, selectedKey) {
  const tabItems = Array.from(tabs);
  const panelItems = Array.from(panels);
  const hasMatch = tabItems.some((tab) => tab.dataset.tabKey === selectedKey);

  if (!hasMatch) return false;

  for (const tab of tabItems) {
    const isSelected = tab.dataset.tabKey === selectedKey;
    tab.classList.toggle('is-active', isSelected);
    tab.setAttribute('aria-selected', String(isSelected));
    tab.setAttribute('aria-expanded', String(isSelected));
    tab.tabIndex = isSelected ? 0 : -1;
  }

  for (const panel of panelItems) {
    panel.hidden = panel.dataset.tabKey !== selectedKey;
  }

  return true;
}

/**
 * Toggles one Pad/MO accordion tab without collapsing siblings.
 * @param {HTMLElement} tab
 * @param {HTMLElement} panel
 * @returns {boolean} whether the panel is open after toggle
 */
export function toggleAccordionTab(tab, panel) {
  const willOpen = panel.hasAttribute('hidden');
  panel.hidden = !willOpen;
  tab.classList.toggle('is-active', willOpen);
  tab.setAttribute('aria-selected', String(willOpen));
  tab.setAttribute('aria-expanded', String(willOpen));
  return willOpen;
}

/**
 * Resets Pad/MO tabs so the first series is expanded and the rest are collapsed.
 * @param {Iterable<HTMLElement>} tabs
 * @param {Iterable<HTMLElement>} panels
 */
export function resetAccordionTabs(tabs, panels) {
  const tabItems = Array.from(tabs);
  const panelItems = Array.from(panels);
  const firstKey = tabItems[0]?.dataset.tabKey;

  for (const tab of tabItems) {
    const isFirst = tab.dataset.tabKey === firstKey;
    tab.classList.toggle('is-active', isFirst);
    tab.setAttribute('aria-selected', String(isFirst));
    tab.setAttribute('aria-expanded', String(isFirst));
    tab.tabIndex = isFirst ? 0 : -1;
  }

  for (const panel of panelItems) {
    panel.hidden = panel.dataset.tabKey !== firstKey;
  }
}

/**
 * Derives overflow arrow availability from the current scroll range.
 * @param {number} scrollStart
 * @param {number} maxScroll
 * @param {number} [epsilon]
 * @returns {{ hasOverflow: boolean, canPrev: boolean, canNext: boolean }}
 */
export function getNavOverflowState(scrollStart, maxScroll, epsilon = 1) {
  const hasOverflow = maxScroll > epsilon;
  if (!hasOverflow) {
    return { hasOverflow: false, canPrev: false, canNext: false };
  }

  return {
    hasOverflow: true,
    canPrev: scrollStart > epsilon,
    canNext: scrollStart < maxScroll - epsilon,
  };
}

const HEADER_V2_DESKTOP_MEDIA = '(min-width: 1280px)';

const HEADER_V2_REVEAL_SELECTOR = [
  '.header-v2__tab',
  '.header-v2__tab-actions > *',
  '.header-v2__collection-entry',
  '.header-v2__variant-card',
  '.header-v2__banner-card',
  '.header-v2__multi-column-title',
  '.header-v2__multi-column-more',
  '.header-v2__multi-column-links > li',
  '.header-v2__multi-column-product',
  '.header-v2__links-promo-entry',
].join(',');

/**
 * Groups elements into visual bands by a rect edge, tolerating sub-pixel drift.
 * @param {HTMLElement[]} elements
 * @param {'top' | 'left'} edge
 * @param {number} [tolerance]
 * @returns {HTMLElement[][]}
 */
export function groupElementsByEdge(elements, edge, tolerance = 12) {
  const measured = elements
    .map((element) => ({ element, value: element.getBoundingClientRect()[edge] }))
    .sort((a, b) => a.value - b.value);

  const groups = [];
  let currentValue = null;

  for (const { element, value } of measured) {
    if (currentValue === null || value - currentValue > tolerance) {
      groups.push([element]);
      currentValue = value;
    } else {
      groups[groups.length - 1].push(element);
    }
  }

  return groups;
}

/**
 * Groups elements into visual rows by top edge, tolerating sub-pixel drift.
 * @param {HTMLElement[]} elements
 * @param {number} [tolerance]
 * @returns {HTMLElement[][]}
 */
export function groupElementsByRow(elements, tolerance = 12) {
  return groupElementsByEdge(elements, 'top', tolerance);
}

/**
 * Groups elements into visual columns by left edge, tolerating sub-pixel drift.
 * @param {HTMLElement[]} elements
 * @param {number} [tolerance]
 * @returns {HTMLElement[][]}
 */
export function groupElementsByColumn(elements, tolerance = 12) {
  return groupElementsByEdge(elements, 'left', tolerance);
}

const browserIsAvailable =
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  typeof window.customElements !== 'undefined';

if (browserIsAvailable) {
  /**
   * Coordinates server-rendered Navigation with private Header V2 Theme Blocks.
   */
  class StoreHeaderV2 extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.abortController = new AbortController();
      this.registry = this.querySelector('[data-header-v2-registry]');
      this.sectionElement = this.closest('.shopify-section');
      this.desktopHost = this.querySelector('[data-header-v2-desktop-host]');
      this.drawer = this.querySelector('[data-header-v2-drawer]');
      this.menuToggle = this.querySelector('[data-header-v2-menu-toggle]');
      this.mobileRoot = this.querySelector('[data-header-v2-mobile-root]');
      this.mobileSpecial = this.querySelector('[data-header-v2-mobile-special]');
      this.mobileHost = this.querySelector('[data-header-v2-mobile-host]');
      this.mobileBackButton = this.querySelector('[data-header-v2-mobile-back]');
      this.desktopMedia = window.matchMedia(HEADER_V2_DESKTOP_MEDIA);
      this.panelOrigins = new Map();
      this.accountCloseTimers = new Map();
      this.activePanel = null;
      this.activeTrigger = null;
      this.isDesktopNavHovered = false;
      this.desktopHostAnimId = 0;
      this.closeTimer = null;
      this.lastScrollTop = window.scrollY;
      this.accumulatedScroll = 0;
      this.isHeaderVisible = true;
      this.isPageScrollLocked = false;
      this.lockedScrollY = 0;

      this.registerPanels();
      this.layoutLinksPromoPanels();
      this.applyDecorations();
      this.bindTabs();
      this.bindTriggers();
      this.bindDesktopNavFillHover();
      this.bindMenuToggle();
      this.bindColumnViewMore();
      this.bindDefaultDisclosures();
      this.bindAccountPopovers();
      this.bindGlobalEvents();
      this.setupTransparency();
      this.syncRegionLabel();
      this.bindDesktopNavOverflow();

      this.isInitialized = true;
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.abortController.abort();
      window.clearTimeout(this.closeTimer);
      this.accountCloseTimers.forEach((timer) => window.clearTimeout(timer));
      this.navResizeObserver?.disconnect();
      this.themeObserver?.disconnect();
      this.closeMobileMenu();
      this.closeSpecialPanel({ restoreFocus: false });
      this.removeGeneratedDecorations();
      // A section reloaded in the theme editor must not leave the page in the hidden-header state.
      this.showHeader();

      this.panelOrigins.clear();
      this.accountCloseTimers.clear();
      this.isInitialized = false;
    }

    /**
     * Registers special panels and remembers their server-rendered positions.
     */
    registerPanels() {
      const panels = this.registry?.querySelectorAll('[data-header-v2-panel]') || [];
      this.panelRegistry = buildFirstMatchMap(panels);

      for (const panel of panels) {
        const marker = document.createComment(`header-v2-panel-${panel.id || 'panel'}`);
        panel.parentNode?.insertBefore(marker, panel);
        this.panelOrigins.set(panel, marker);
        panel.hidden = true;
      }

      if (window.Shopify?.designMode) {
        const configuredCount = Array.from(panels).filter((panel) =>
          normalizeMenuTitle(panel.dataset.menuTitle),
        ).length;
        const menuTitles = new Set(
          Array.from(
            this.querySelectorAll('[data-header-v2-trigger][data-menu-level="primary"]'),
          ).map((trigger) => normalizeMenuTitle(trigger.dataset.menuTitle)),
        );

        if (configuredCount !== this.panelRegistry.size) {
          console.warn('[Header V2] Duplicate panel titles detected; the first configured block is used.');
        }

        for (const title of this.panelRegistry.keys()) {
          if (!menuTitles.has(title)) {
            console.warn(`[Header V2] No primary Navigation item matches panel title "${title}".`);
          }
        }
      }
    }

    /**
     * Splits ordered link entries into image cards and overflow text links.
     */
    layoutLinksPromoPanels() {
      for (const panel of this.querySelectorAll('[data-header-v2-links-promo]')) {
        const featuredHost = panel.querySelector('[data-header-v2-links-promo-featured]');
        const textHost = panel.querySelector('[data-header-v2-links-promo-text]');
        const source = panel.querySelector('[data-header-v2-links-promo-source]');
        if (!featuredHost || !textHost || !source) continue;

        const featuredCount = Math.min(4, Math.max(1, Number(panel.dataset.featuredCount) || 4));
        const entries = [...featuredHost.children, ...textHost.children, ...source.children];

        entries.forEach((entry, index) => {
          const host = index < featuredCount ? featuredHost : textHost;
          host.append(entry);
        });

        // Whitespace text nodes keep :empty from matching, so toggle hosts here.
        source.hidden = true;
        featuredHost.hidden = !featuredHost.childElementCount;
        textHost.hidden = !textHost.childElementCount;
      }
    }

    /**
     * Applies optional tracking and visual markers to matching menu triggers.
     */
    applyDecorations() {
      const decorations = this.registry?.querySelectorAll('[data-header-v2-decoration]') || [];
      const decorationRegistry = new Map();
      let configuredDecorationCount = 0;

      for (const decoration of decorations) {
        const level = decoration.dataset.targetLevel || 'primary';
        const title = normalizeMenuTitle(decoration.dataset.menuTitle);
        const key = `${level}:${title}`;
        if (!title) continue;
        configuredDecorationCount += 1;
        if (!decorationRegistry.has(key)) decorationRegistry.set(key, decoration);
      }

      const matchedDecorations = new Set();
      for (const trigger of this.querySelectorAll('[data-header-v2-trigger]')) {
        const level = trigger.dataset.menuLevel || 'primary';
        const key = `${level}:${normalizeMenuTitle(trigger.dataset.menuTitle)}`;
        const decoration = decorationRegistry.get(key);
        if (!decoration) continue;
        matchedDecorations.add(key);

        if (decoration.dataset.gtaName) {
          trigger.dataset.gta = decoration.dataset.gtaName;
          trigger.classList.add('click-gta');
        }

        const template = decoration.querySelector('template[data-header-v2-badge-template]');
        if (template?.content.childElementCount) {
          const badge = template.content.firstElementChild.cloneNode(true);
          badge.dataset.headerV2GeneratedBadge = '';
          const label = trigger.querySelector(':scope > .header-v2__nav-label');
          // Dots sit before the title. Text/image badges stay on the title so after-position counts as content width.
          if (label && badge.classList.contains('header-v2__nav-badge--dot')) {
            label.prepend(badge);
            label.classList.add('header-v2__nav-label--leading-dot');
          } else if (label) {
            label.append(badge);
          } else {
            trigger.append(badge);
          }
        }
      }

      if (window.Shopify?.designMode) {
        if (configuredDecorationCount !== decorationRegistry.size) {
          console.warn(
            '[Header V2] Duplicate decoration targets detected; the first configured block is used.',
          );
        }
        for (const key of decorationRegistry.keys()) {
          if (!matchedDecorations.has(key)) {
            console.warn(`[Header V2] No Navigation item matches decoration target "${key}".`);
          }
        }
      }
    }

    /**
     * Removes presentation nodes generated from decoration configuration blocks.
     */
    removeGeneratedDecorations() {
      document.querySelectorAll('[data-header-v2-generated-badge]').forEach((badge) => {
        badge.parentElement?.classList.remove('header-v2__nav-label--leading-dot');
        badge.remove();
      });
    }

    /**
     * Connects each server-rendered trigger to a matching special panel when present.
     */
    bindTriggers() {
      const { signal } = this.abortController;

      for (const trigger of this.querySelectorAll(
        '[data-header-v2-trigger][data-menu-level="primary"]',
      )) {
        const panel = this.panelRegistry.get(normalizeMenuTitle(trigger.dataset.menuTitle));
        if (!panel) {
        trigger.addEventListener(
          'pointerenter',
          () => {
            if (this.shouldUseHover(trigger)) this.closeSpecialPanel();
          },
          { signal },
        );
          continue;
        }

        trigger.dataset.headerV2SpecialTrigger = '';
        trigger.setAttribute('aria-haspopup', 'true');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', panel.id);

        const expandAffordance = trigger.querySelector('[data-header-v2-affordance="expand"]');
        const linkAffordance = trigger.querySelector('[data-header-v2-affordance="link"]');
        if (expandAffordance && linkAffordance) {
          expandAffordance.hidden = false;
          linkAffordance.hidden = true;
        }

        trigger.addEventListener('click', (event) => this.handleSpecialTriggerClick(event, trigger, panel), {
          signal,
        });

        trigger.addEventListener(
          'pointerenter',
          () => {
            if (this.shouldUseHover(trigger) && !this.isProductAnchorBarPinned()) {
              this.openSpecialPanel(trigger, panel, 'desktop');
            }
          },
          { signal },
        );

        // An anchor jump can move the header under a stationary pointer. Require actual pointer movement
        // while the product bar is pinned, so that layout-driven pointerenter cannot cover the next tab.
        trigger.addEventListener(
          'pointermove',
          () => {
            if (
              this.activeTrigger !== trigger &&
              this.shouldUseHover(trigger) &&
              this.isProductAnchorBarPinned()
            ) {
              this.openSpecialPanel(trigger, panel, 'desktop');
            }
          },
          { signal },
        );

        trigger.addEventListener(
          'pointerleave',
          (event) => {
            if (this.shouldUseHover(trigger) && !this.desktopHost?.contains(event.relatedTarget)) {
              this.scheduleSpecialPanelClose();
            }
          },
          { signal },
        );
      }

      this.desktopHost?.addEventListener('pointerenter', () => window.clearTimeout(this.closeTimer), {
        signal,
      });
      this.desktopHost?.addEventListener('pointerleave', () => this.scheduleSpecialPanelClose(), {
        signal,
      });
    }

    /**
     * PC transparent header: hovering the desktop nav row (not logo or cart)
     * restores the filled background, even when the item has no mega panel.
     */
    bindDesktopNavFillHover() {
      const nav = this.querySelector('[data-header-v2-desktop-nav]');
      if (!nav) return;

      const { signal } = this.abortController;
      nav.addEventListener(
        'pointerenter',
        () => {
          if (!this.desktopMedia.matches) return;
          this.isDesktopNavHovered = true;
          this.syncHeaderFill();
        },
        { signal },
      );
      nav.addEventListener(
        'pointerleave',
        () => {
          this.isDesktopNavHovered = false;
          this.syncHeaderFill();
        },
        { signal },
      );
    }

    /**
     * Toggles the Pad/MO full-screen panel from the header hamburger/close control.
     */
    bindMenuToggle() {
      const { signal } = this.abortController;
      this.menuToggle?.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          if (this.classList.contains('is-mobile-menu-open')) {
            this.closeMobileMenu();
          } else {
            this.openMobileMenu();
          }
        },
        { signal },
      );
    }

    /**
     * Opens the Pad/MO overlay: the header stays on screen and the panel fills the rest.
     */
    openMobileMenu() {
      if (!this.drawer || this.desktopMedia.matches) return;

      this.accumulatedScroll = 0;
      this.showHeader();
      this.style.transform = 'none';

      this.drawer.hidden = false;
      this.classList.add('is-mobile-menu-open');
      this.menuToggle?.setAttribute('aria-expanded', 'true');
      this.syncMenuToggleIcons(true);
      document.documentElement.classList.add('header-v2-menu-open');
      this.sectionElement?.style.setProperty('z-index', 'var(--z-index-drawer)');
      this.lockPageScroll();
      this.syncHeaderFill();
    }

    /**
     * Closes the Pad/MO panel and restores the hamburger icon.
     */
    closeMobileMenu() {
      if (!this.classList.contains('is-mobile-menu-open') && this.drawer?.hidden !== false) {
        this.syncMenuToggleIcons(false);
        return;
      }

      this.closeMobileSpecialPanel();
      if (this.drawer) this.drawer.hidden = true;
      this.classList.remove('is-mobile-menu-open');
      this.menuToggle?.setAttribute('aria-expanded', 'false');
      this.syncMenuToggleIcons(false);
      document.documentElement.classList.remove('header-v2-menu-open');
      this.sectionElement?.style.removeProperty('z-index');
      this.unlockPageScroll();
      this.syncHeaderFill();
    }

    /**
     * Freezes the document without overflow:hidden on html, which unsticks the header on iOS.
     */
    lockPageScroll() {
      if (this.isPageScrollLocked) return;
      this.lockedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.lockedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      this.isPageScrollLocked = true;
    }

    /**
     * Restores the scroll position captured when the Pad/MO menu opened.
     */
    unlockPageScroll() {
      if (!this.isPageScrollLocked) return;
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('left');
      document.body.style.removeProperty('right');
      document.body.style.removeProperty('width');
      window.scrollTo(0, this.lockedScrollY || 0);
      this.isPageScrollLocked = false;
    }

    /**
     * Swaps hamburger and close glyphs on the menu control.
     * @param {boolean} isOpen
     */
    syncMenuToggleIcons(isOpen) {
      const hamburger = this.menuToggle?.querySelector('[data-header-v2-menu-icon="hamburger"]');
      const close = this.menuToggle?.querySelector('[data-header-v2-menu-icon="close"]');
      if (hamburger) hamburger.hidden = Boolean(isOpen);
      if (close) close.hidden = !isOpen;
    }

    /**
     * Fills the country-select label from the shared theme region payload.
     */
    syncRegionLabel() {
      const regionText = this.querySelector('[data-header-v2-region-text]');
      const regionName = window.themeVariables?.globalData?.currentRegionInfo;
      if (regionText && regionName) regionText.textContent = regionName;
    }

    /**
     * Adds exclusive PC tabs and multi-open Pad/MO accordion tabs.
     */
    bindTabs() {
      const { signal } = this.abortController;

      for (const tabsComponent of this.querySelectorAll('[data-header-v2-tabs]')) {
        const tabs = tabsComponent.querySelectorAll('[data-header-v2-tab]');
        const panels = tabsComponent.querySelectorAll('[data-header-v2-tab-panel]');

        tabsComponent.addEventListener(
          'click',
          (event) => {
            const tab = event.target.closest('[data-header-v2-tab]');
            if (!tab || !tabsComponent.contains(tab)) return;

            // Drawer is always multi-open accordion; PC mega is exclusive.
            const useAccordion =
              !this.desktopMedia.matches || Boolean(tab.closest('[data-header-v2-drawer]'));

            if (!useAccordion) {
              updateTabSelection(tabs, panels, tab.dataset.tabKey);
              return;
            }

            const panel = tabsComponent.querySelector(
              `[data-header-v2-tab-panel][data-tab-key="${tab.dataset.tabKey}"]`,
            );
            if (!panel) return;
            toggleAccordionTab(tab, panel);
          },
          { signal },
        );

        tabsComponent.addEventListener(
          'pointerover',
          (event) => {
            if (!this.desktopMedia.matches || !window.matchMedia('(pointer: fine)').matches) return;
            const tab = event.target.closest('[data-header-v2-tab]');
            if (!tab || !tabsComponent.contains(tab)) return;
            if (tab.closest('[data-header-v2-drawer]')) return;
            updateTabSelection(tabs, panels, tab.dataset.tabKey);
          },
          { signal },
        );

        tabsComponent.addEventListener(
          'keydown',
          (event) => {
            const tab = event.target.closest('[data-header-v2-tab]');
            if (!tab || !tabsComponent.contains(tab)) return;

            const useAccordion =
              !this.desktopMedia.matches || Boolean(tab.closest('[data-header-v2-drawer]'));
            const tabItems = Array.from(tabs);
            const currentIndex = tabItems.indexOf(tab);
            let nextIndex = null;

            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
              nextIndex = (currentIndex + 1) % tabItems.length;
            } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
              nextIndex = (currentIndex - 1 + tabItems.length) % tabItems.length;
            } else if (event.key === 'Home') {
              nextIndex = 0;
            } else if (event.key === 'End') {
              nextIndex = tabItems.length - 1;
            } else if (event.key === 'Enter' || event.key === ' ') {
              if (useAccordion) {
                event.preventDefault();
                const panel = tabsComponent.querySelector(
                  `[data-header-v2-tab-panel][data-tab-key="${tab.dataset.tabKey}"]`,
                );
                if (panel) toggleAccordionTab(tab, panel);
              }
              return;
            }

            if (nextIndex === null) return;
            event.preventDefault();
            const nextTab = tabItems[nextIndex];
            if (!useAccordion) {
              updateTabSelection(tabs, panels, nextTab.dataset.tabKey);
            }
            nextTab.focus();
          },
          { signal },
        );
      }
    }

    /**
     * Preserves the legacy hover account portal while retaining native click behavior.
     */
    bindAccountPopovers() {
      const { signal } = this.abortController;

      for (const popover of this.querySelectorAll('.header-v2__account-popover')) {
        popover.addEventListener(
          'pointerenter',
          () => {
            if (!this.desktopMedia.matches || !window.matchMedia('(pointer: fine)').matches) return;
            window.clearTimeout(this.accountCloseTimers.get(popover));
            popover.open = true;
          },
          { signal },
        );

        popover.addEventListener(
          'pointerleave',
          () => {
            if (!this.desktopMedia.matches || !window.matchMedia('(pointer: fine)').matches) return;
            const timer = window.setTimeout(() => {
              popover.open = false;
              this.accountCloseTimers.delete(popover);
            }, 200);
            this.accountCloseTimers.set(popover, timer);
          },
          { signal },
        );
      }
    }

    /**
     * View all lives in the column heading row (inside `<summary>`) so PC can
     * keep it on one line with the title. Prevent that click from toggling
     * the column, and navigate only when the control is a real link.
     */
    bindColumnViewMore() {
      const { signal } = this.abortController;

      this.addEventListener(
        'click',
        (event) => {
          const more = event.target.closest('.header-v2__multi-column-more');
          if (!more?.closest('summary') || !more.closest('[data-header-v2-column-disclosure]')) return;

          const column = more.closest('[data-header-v2-column-disclosure]');
          event.preventDefault();
          if (this.desktopMedia.matches) column.open = true;

          if (!(more instanceof HTMLAnchorElement)) return;

          const href = more.getAttribute('href')?.trim();
          if (!href || href === '#') return;

          window.location.assign(href);
        },
        { capture: true, signal },
      );
    }

    /**
     * Preserves native details as the no-JavaScript fallback while adding hover behavior.
     */
    bindDefaultDisclosures() {
      const { signal } = this.abortController;

      for (const disclosure of this.querySelectorAll('[data-header-v2-default-disclosure]')) {
        const summary = disclosure.querySelector(':scope > summary');
        if (!summary) continue;

        disclosure.addEventListener(
          'pointerenter',
          () => {
            if (summary.hasAttribute('data-header-v2-special-trigger')) return;
            if (this.shouldUseHover(summary)) {
              this.closeSiblingDisclosures(disclosure);
              disclosure.open = true;
            }
          },
          { signal },
        );

        disclosure.addEventListener(
          'pointerleave',
          () => {
            if (summary.hasAttribute('data-header-v2-special-trigger') || !this.shouldUseHover(summary)) return;
            window.setTimeout(() => {
              if (!disclosure.matches(':hover')) disclosure.open = false;
            }, 250);
          },
          { signal },
        );

        summary.addEventListener(
          'click',
          (event) => {
            if (summary.hasAttribute('data-header-v2-special-trigger')) return;
            if (this.shouldUseHover(summary) && summary.dataset.url) {
              event.preventDefault();
              window.location.href = summary.dataset.url;
            } else if (this.isMobileContext(summary)) {
              this.preserveDrawerAnchorPosition(summary, () => {
                this.closeSiblingDisclosures(disclosure);
                this.closeMobileSpecialPanel();
              });
            } else {
              this.closeSiblingDisclosures(disclosure);
            }
          },
          { signal },
        );
      }
    }

    /**
     * Enables desktop primary-nav overflow arrows when items exceed the remaining bar space.
     */
    bindDesktopNavOverflow() {
      this.desktopNav = this.querySelector('[data-header-v2-desktop-nav]');
      this.navViewport = this.querySelector('[data-header-v2-nav-viewport]');
      this.navList = this.querySelector('[data-header-v2-nav-list]');
      this.navArrows = this.querySelector('[data-header-v2-nav-arrows]');
      this.navPrev = this.querySelector('[data-header-v2-nav-prev]');
      this.navNext = this.querySelector('[data-header-v2-nav-next]');
      this.navScrollOffset = 0;

      if (!this.desktopNav || !this.navViewport || !this.navList || !this.navArrows) return;

      const { signal } = this.abortController;
      this.navResizeObserver = new ResizeObserver(() => this.syncDesktopNavOverflow());
      this.navResizeObserver.observe(this.navViewport);
      this.navResizeObserver.observe(this.navList);

      this.navPrev?.addEventListener('click', () => this.scrollDesktopNav(-1), { signal });
      this.navNext?.addEventListener('click', () => this.scrollDesktopNav(1), { signal });
      this.desktopMedia.addEventListener('change', () => this.syncDesktopNavOverflow(), { signal });

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (this.isInitialized) this.syncDesktopNavOverflow();
        });
      }

      this.syncDesktopNavOverflow();
    }

    /**
     * @returns {{ maxScroll: number, isRtl: boolean }}
     */
    getDesktopNavMetrics() {
      const viewportWidth = this.navViewport?.clientWidth || 0;
      const listWidth = this.navList?.scrollWidth || 0;
      const isRtl = getComputedStyle(this.navViewport || this).direction === 'rtl';
      return {
        maxScroll: Math.max(0, listWidth - viewportWidth),
        isRtl,
      };
    }

    /**
     * Toggles the arrow pair and disabled states from the current overflow range.
     */
    syncDesktopNavOverflow() {
      if (!this.desktopNav || !this.navViewport || !this.navList || !this.navArrows) return;

      if (!this.desktopMedia.matches) {
        this.navScrollOffset = 0;
        this.navList.style.transform = '';
        this.desktopNav.removeAttribute('data-overflow');
        this.desktopNav.removeAttribute('data-overflow-start');
        this.desktopNav.removeAttribute('data-overflow-end');
        this.navArrows.hidden = true;
        if (this.navPrev) this.navPrev.disabled = true;
        if (this.navNext) this.navNext.disabled = true;
        return;
      }

      const { maxScroll } = this.getDesktopNavMetrics();
      this.navScrollOffset = Math.min(Math.max(0, this.navScrollOffset), maxScroll);
      const state = getNavOverflowState(this.navScrollOffset, maxScroll);

      this.navArrows.hidden = !state.hasOverflow;
      this.desktopNav.toggleAttribute('data-overflow', state.hasOverflow);
      this.desktopNav.toggleAttribute('data-overflow-start', state.canPrev);
      this.desktopNav.toggleAttribute('data-overflow-end', state.canNext);
      if (this.navPrev) this.navPrev.disabled = !state.canPrev;
      if (this.navNext) this.navNext.disabled = !state.canNext;

      const { isRtl } = this.getDesktopNavMetrics();
      const offset = this.navScrollOffset * (isRtl ? 1 : -1);
      this.navList.style.transform = this.navScrollOffset ? `translateX(${offset}px)` : '';
    }

    /**
     * Slides the primary nav by one viewport page in the requested direction.
     * @param {number} direction
     */
    scrollDesktopNav(direction) {
      if (!this.navViewport || !direction) return;

      const { maxScroll } = this.getDesktopNavMetrics();
      const step = Math.max(this.navViewport.clientWidth * 0.7, 120);
      this.navScrollOffset = Math.min(Math.max(0, this.navScrollOffset + direction * step), maxScroll);
      this.syncDesktopNavOverflow();
    }

    /**
     * Binds document, drawer, editor, resize, and scroll lifecycle events.
     */
    bindGlobalEvents() {
      const { signal } = this.abortController;

      document.addEventListener(
        'pointerdown',
        (event) => {
          for (const popover of this.querySelectorAll('.header-v2__account-popover[open]')) {
            if (!popover.contains(event.target)) popover.open = false;
          }

          if (!this.activePanel || this.isMobileContext(this.activeTrigger)) return;
          const isInsidePanel = this.desktopHost?.contains(event.target);
          const isSpecialTrigger = event.target.closest?.('[data-header-v2-special-trigger]');
          if (!isInsidePanel && !isSpecialTrigger) this.closeSpecialPanel();
        },
        { signal },
      );

      this.addEventListener(
        'focusout',
        () => {
          queueMicrotask(() => {
            if (
              this.activePanel &&
              !this.isMobileContext(this.activeTrigger) &&
              !this.contains(document.activeElement)
            ) {
              this.closeSpecialPanel();
            }
          });
        },
        { signal },
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape') return;
          this.querySelectorAll('.header-v2__account-popover[open]').forEach((popover) => {
            popover.open = false;
          });
          this.closeSpecialPanel();
          this.closeMobileMenu();
          this.closeDefaultDisclosures();
        },
        { signal },
      );

      document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), {
        signal,
      });
      document.addEventListener('shopify:block:deselect', () => this.closeSpecialPanel(), { signal });

      this.mobileBackButton?.addEventListener('click', () => this.closeMobileSpecialPanel(), { signal });
      this.querySelector('[data-header-v2-panel-close]')?.addEventListener(
        'click',
        () => this.closeSpecialPanel({ restoreFocus: true }),
        { signal },
      );

      this.desktopMedia.addEventListener(
        'change',
        () => {
          this.isDesktopNavHovered = false;
          this.closeSpecialPanel({ restoreFocus: false });
          this.closeMobileMenu();
        },
        { signal },
      );

      if (this.hasAttribute('hide-on-scroll') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        window.addEventListener('scroll', () => this.handleHeaderScroll(), { passive: true, signal });
      }

      window.addEventListener('scroll', () => this.syncHeaderFill(), { passive: true, signal });

      if (window.Shopify?.designMode) {
        const refreshTransparency = () => this.setupTransparency();
        document.addEventListener('shopify:section:load', refreshTransparency, { signal });
        document.addEventListener('shopify:section:unload', refreshTransparency, { signal });
        document.addEventListener('shopify:section:reorder', refreshTransparency, { signal });
      }
    }

    /**
     * Handles click behavior for special panels across hover, click, and drawer contexts.
     * @param {MouseEvent} event
     * @param {HTMLElement} trigger
     * @param {HTMLElement} panel
     */
    handleSpecialTriggerClick(event, trigger, panel) {
      if (this.isMobileContext(trigger)) {
        event.preventDefault();
        event.stopPropagation();
        if (this.activePanel === panel && this.activeTrigger === trigger) {
          this.closeMobileSpecialPanel();
        } else {
          this.openSpecialPanel(trigger, panel, 'mobile');
        }
        return;
      }

      if (this.shouldUseHover(trigger)) {
        const url = trigger.dataset.url || trigger.getAttribute('href');
        if (url && url !== '#') {
          event.preventDefault();
          window.location.href = url;
        }
        return;
      }

      event.preventDefault();
      if (this.activePanel === panel) {
        this.closeSpecialPanel();
      } else {
        this.openSpecialPanel(trigger, panel, 'desktop');
      }
    }

    /**
     * Resolves (or creates) an inline mount point under the primary drawer item.
     * @param {HTMLElement} trigger
     * @returns {HTMLElement | null}
     */
    getMobileInlineHost(trigger) {
      const item = trigger.closest('.header-v2__mobile-item');
      if (!item) return this.mobileHost;

      let host = item.querySelector(':scope > [data-header-v2-mobile-inline-host]');
      if (!host) {
        host = document.createElement('div');
        host.className = 'header-v2__mobile-inline-host';
        host.setAttribute('data-header-v2-mobile-inline-host', '');
        item.append(host);
      }

      return host;
    }

    /**
     * On Pad/MO, open the first series tab and collapse the rest when a primary panel mounts.
     * @param {HTMLElement} panel
     */
    resetMobileTabsAccordion(panel) {
      if (!panel?.hasAttribute('data-header-v2-tabs')) return;
      const tabs = panel.querySelectorAll('[data-header-v2-tab]');
      const panels = panel.querySelectorAll('[data-header-v2-tab-panel]');
      if (!tabs.length || !panels.length) return;
      resetAccordionTabs(tabs, panels);
    }

    /**
     * On Pad/MO, collapse every multi column column except the first one.
     * @param {HTMLElement} panel
     */
    resetMobileColumnDisclosures(panel) {
      const columns = panel.querySelectorAll('[data-header-v2-column-disclosure]');
      columns.forEach((column, index) => {
        column.open = index === 0;
      });
    }

    /**
     * Reopens every column so PC always renders the full multi column layout.
     * @param {HTMLElement} panel
     */
    restoreColumnDisclosures(panel) {
      for (const column of panel.querySelectorAll('[data-header-v2-column-disclosure]')) {
        column.open = true;
      }
    }

    /**
     * Keeps the primary row visible and hides the fallback submenu while a special panel is open.
     * @param {HTMLElement | null | undefined} trigger
     */
    prepareMobileSpecialPresentation(trigger) {
      if (!trigger) return;

      const item = trigger.closest('.header-v2__mobile-item');
      const disclosure = trigger.closest('details.header-v2__mobile-disclosure');
      item?.classList.add('is-special-expanded');

      if (disclosure) {
        disclosure.open = true;
        disclosure.classList.add('is-special-expanded');
        const submenu = disclosure.querySelector(':scope > .header-v2__mobile-submenu');
        if (submenu) submenu.hidden = true;
      }
    }

    /**
     * Restores the primary drawer item after an inline special panel closes.
     * @param {HTMLElement | null | undefined} trigger
     */
    clearMobileSpecialPresentation(trigger) {
      if (!trigger) return;

      const item = trigger.closest('.header-v2__mobile-item');
      const disclosure = trigger.closest('details.header-v2__mobile-disclosure');
      const inlineHost = item?.querySelector(':scope > [data-header-v2-mobile-inline-host]');

      item?.classList.remove('is-special-expanded');
      if (inlineHost) inlineHost.hidden = true;

      if (disclosure) {
        disclosure.open = false;
        disclosure.classList.remove('is-special-expanded');
        const submenu = disclosure.querySelector(':scope > .header-v2__mobile-submenu');
        if (submenu) submenu.hidden = false;
      }
    }

    /**
     * Opens a registered panel inside the active responsive host.
     * @param {HTMLElement} trigger
     * @param {HTMLElement} panel
     * @param {'desktop' | 'mobile'} context
     */
    openSpecialPanel(trigger, panel, context) {
      window.clearTimeout(this.closeTimer);

      if (context === 'mobile') {
        this.preserveDrawerAnchorPosition(trigger, () => {
          this.mountSpecialPanel(trigger, panel, context);
        });
        return;
      }

      this.mountSpecialPanel(trigger, panel, context);
    }

    /**
     * Mounts a special panel into the desktop mega host or Pad/MO inline host.
     * @param {HTMLElement} trigger
     * @param {HTMLElement} panel
     * @param {'desktop' | 'mobile'} context
     */
    mountSpecialPanel(trigger, panel, context) {
      this.closeDefaultDisclosures();

      const isDesktopSwitch =
        context === 'desktop' && Boolean(this.activePanel && this.activePanel !== panel);
      const wasDesktopHostHidden = context === 'desktop' && Boolean(this.desktopHost?.hidden);

      if (this.activePanel && this.activePanel !== panel) {
        this.clearMobileSpecialPresentation(this.activeTrigger);
        if (this.activeTrigger && this.activeTrigger !== trigger) {
          this.activeTrigger.setAttribute('aria-expanded', 'false');
        }
        this.restorePanel(this.activePanel);
      }

      const host = context === 'mobile' ? this.getMobileInlineHost(trigger) : this.desktopHost;
      if (!host) return;

      if (context === 'desktop' && !wasDesktopHostHidden) {
        host.style.height = `${host.getBoundingClientRect().height}px`;
      }

      host.append(panel);
      panel.hidden = false;
      host.hidden = false;
      this.activePanel = panel;

      if (this.activeTrigger && this.activeTrigger !== trigger) {
        this.activeTrigger.setAttribute('aria-expanded', 'false');
      }
      this.activeTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');

      if (context === 'mobile') {
        // Keep the root list visible so expansion stays stair-step / accordion.
        if (this.mobileRoot) this.mobileRoot.hidden = false;
        if (this.mobileSpecial) this.mobileSpecial.hidden = true;
        this.prepareMobileSpecialPresentation(trigger);
        this.resetMobileTabsAccordion(panel);
        this.resetMobileColumnDisclosures(panel);
        this.playPanelReveal(panel);
        this.syncHeaderFill();
        return;
      }

      document.documentElement.classList.add('header-v2-menu-open');
      this.classList.add('has-open-panel');
      this.sectionElement?.style.setProperty('z-index', 'var(--z-index-drawer)');

      if (wasDesktopHostHidden) host.style.height = '0px';
      const targetHeight = this.measureDesktopPanelHeight(panel);
      this.animateDesktopHostHeight(targetHeight).then((animId) => {
        if (this.desktopHostAnimId !== animId || this.activePanel !== panel) return;
        const maxHeight = this.getDesktopHostMaxHeight();
        this.desktopHost?.classList.toggle('is-scrollable', panel.scrollHeight > maxHeight + 1);
      });

      if (!isDesktopSwitch) this.playPanelReveal(panel);
      this.syncHeaderFill();
    }

    /**
     * Reads the computed max height of the desktop mega host.
     * @returns {number}
     */
    getDesktopHostMaxHeight() {
      if (!this.desktopHost) return window.innerHeight * 0.75;
      const parsed = Number.parseFloat(getComputedStyle(this.desktopHost).maxHeight);
      return Number.isFinite(parsed) ? parsed : window.innerHeight * 0.75;
    }

    /**
     * Measures the visible height a desktop panel should occupy.
     * @param {HTMLElement} panel
     * @returns {number}
     */
    measureDesktopPanelHeight(panel) {
      return Math.min(panel.scrollHeight, this.getDesktopHostMaxHeight());
    }

    /**
     * Animates the desktop mega host height. Shrinking clips from the bottom
     * (upward retract); growing reveals downward.
     * @param {number} targetHeight
     * @returns {Promise<number>}
     */
    animateDesktopHostHeight(targetHeight) {
      const host = this.desktopHost;
      const animId = ++this.desktopHostAnimId;
      if (!host) return Promise.resolve(animId);

      const next = Math.max(0, targetHeight);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      host.classList.remove('is-scrollable');

      if (reduceMotion) {
        host.style.height = `${next}px`;
        return Promise.resolve(animId);
      }

      const from = host.getBoundingClientRect().height;
      host.style.height = `${from}px`;
      void host.offsetHeight;
      host.style.height = `${next}px`;

      if (Math.abs(from - next) < 0.5) return Promise.resolve(animId);

      return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          host.removeEventListener('transitionend', onEnd);
          window.clearTimeout(timeoutId);
          resolve(animId);
        };
        const onEnd = (event) => {
          if (event.target !== host || event.propertyName !== 'height') return;
          finish();
        };
        host.addEventListener('transitionend', onEnd);
        const durationMs = Number.parseFloat(getComputedStyle(host).transitionDuration) * 1000 || 200;
        const timeoutId = window.setTimeout(finish, durationMs + 80);
      });
    }

    /**
     * Staggers panel content on two axes at once: rows top-to-bottom, columns left-to-right.
     * @param {HTMLElement} panel
     */
    playPanelReveal(panel) {
      this.clearPanelReveal(panel);
      // Staggered reveal is a PC-only effect; the drawer expands without it.
      if (panel.closest('[data-header-v2-drawer]')) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const candidates = Array.from(panel.querySelectorAll(HEADER_V2_REVEAL_SELECTOR)).filter(
        (element) => element.getClientRects().length > 0,
      );
      if (!candidates.length) return;

      groupElementsByRow(candidates).forEach((row, rowIndex) => {
        for (const element of row) {
          element.dataset.headerV2Reveal = '';
          element.style.setProperty('--header-v2-reveal-row', String(rowIndex));
        }
      });

      groupElementsByColumn(candidates).forEach((column, columnIndex) => {
        for (const element of column) {
          element.style.setProperty('--header-v2-reveal-column', String(columnIndex));
        }
      });

      // Restart the animation when the same panel reopens.
      void panel.offsetWidth;
      panel.classList.add('is-revealing');
    }

    /**
     * Removes reveal markers so a reopened panel animates from a clean state.
     * @param {HTMLElement} panel
     */
    clearPanelReveal(panel) {
      panel.classList.remove('is-revealing');

      for (const element of panel.querySelectorAll('[data-header-v2-reveal]')) {
        delete element.dataset.headerV2Reveal;
        element.style.removeProperty('--header-v2-reveal-row');
        element.style.removeProperty('--header-v2-reveal-column');
      }
    }

    /**
     * Schedules hover panels to close after the pointer bridge tolerance.
     */
    scheduleSpecialPanelClose() {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => this.closeSpecialPanel(), 500);
    }

    /**
     * Closes the active special panel and restores its Theme Block to the registry.
     * @param {{restoreFocus?: boolean}} options
     */
    closeSpecialPanel({ restoreFocus = false } = {}) {
      window.clearTimeout(this.closeTimer);
      if (!this.activePanel) return;

      const shouldAnimateDesktop =
        this.desktopMedia.matches && this.desktopHost && !this.desktopHost.hidden;

      if (shouldAnimateDesktop) {
        const closingPanel = this.activePanel;
        this.animateDesktopHostHeight(0).then((animId) => {
          if (this.desktopHostAnimId !== animId || this.activePanel !== closingPanel) return;
          this.finalizeSpecialPanelClose({ restoreFocus });
        });
        return;
      }

      this.finalizeSpecialPanelClose({ restoreFocus });
    }

    /**
     * Restores registry content and hides hosts after a panel close.
     * @param {{restoreFocus?: boolean}} options
     */
    finalizeSpecialPanelClose({ restoreFocus = false } = {}) {
      if (!this.activePanel) return;

      const trigger = this.activeTrigger;
      this.clearMobileSpecialPresentation(trigger);
      this.restorePanel(this.activePanel);
      this.activePanel = null;
      this.activeTrigger = null;
      if (this.desktopHost) {
        this.desktopHost.hidden = true;
        this.desktopHost.style.height = '0px';
        this.desktopHost.classList.remove('is-scrollable');
      }
      this.mobileHost && (this.mobileHost.hidden = true);
      trigger?.setAttribute('aria-expanded', 'false');

      this.classList.remove('has-open-panel');
      if (!this.classList.contains('is-mobile-menu-open')) {
        document.documentElement.classList.remove('header-v2-menu-open');
        this.sectionElement?.style.removeProperty('z-index');
      }
      this.syncHeaderFill();

      if (restoreFocus) trigger?.focus();
    }

    /**
     * Closes an inline mobile special panel and keeps the root list visible.
     */
    closeMobileSpecialPanel() {
      this.closeSpecialPanel();
      if (this.mobileRoot) this.mobileRoot.hidden = false;
      if (this.mobileSpecial) this.mobileSpecial.hidden = true;
    }

    /**
     * Returns a panel to its original content_for registry position.
     * @param {HTMLElement} panel
     */
    restorePanel(panel) {
      const marker = this.panelOrigins.get(panel);
      this.clearPanelReveal(panel);
      this.restoreColumnDisclosures(panel);
      panel.hidden = true;

      if (marker?.parentNode) {
        marker.parentNode.insertBefore(panel, marker.nextSibling);
      } else {
        this.registry?.append(panel);
      }
    }

    /**
     * Closes all native fallback disclosures except an optional current disclosure.
     * @param {HTMLDetailsElement | null} current
     */
    closeDefaultDisclosures(current = null) {
      const disclosures = new Set([
        ...this.querySelectorAll('[data-header-v2-default-disclosure][open]'),
        ...(this.drawer?.querySelectorAll('[data-header-v2-default-disclosure][open]') || []),
      ]);

      for (const disclosure of disclosures) {
        if (disclosure !== current) disclosure.open = false;
      }
    }

    /**
     * Closes only disclosures at the same Navigation depth.
     * @param {HTMLDetailsElement} current
     */
    closeSiblingDisclosures(current) {
      const list = current.closest('ul');
      if (!list) return;

      for (const disclosure of list.querySelectorAll(':scope > li > details[open]')) {
        if (disclosure !== current) disclosure.open = false;
      }
    }

    /**
     * Keep the tapped L1 header at the same visual Y after exclusive accordion
     * layout changes inside the Pad/MO drawer scroller.
     * @param {HTMLElement} anchor
     * @param {() => void} mutate
     */
    preserveDrawerAnchorPosition(anchor, mutate) {
      const scroller =
        this.drawer?.querySelector('.header-v2__mobile-scroll') ||
        anchor?.closest('.header-v2__mobile-scroll');
      if (!anchor || !scroller) {
        mutate();
        return;
      }

      const before = anchor.getBoundingClientRect().top;
      mutate();

      const sync = () => {
        if (!anchor.isConnected) return;
        const delta = anchor.getBoundingClientRect().top - before;
        if (Math.abs(delta) >= 1) scroller.scrollTop += delta;
      };

      sync();
      requestAnimationFrame(() => {
        sync();
        requestAnimationFrame(sync);
      });
    }

    /**
     * Returns whether a trigger should use pointer hover semantics.
     * @param {HTMLElement} trigger
     * @returns {boolean}
     */
    shouldUseHover(trigger) {
      return (
        this.dataset.menuTrigger === 'hover' &&
        this.desktopMedia.matches &&
        !this.isMobileContext(trigger) &&
        window.matchMedia('(pointer: fine)').matches
      );
    }

    /**
     * Detects triggers rendered inside the shared navigation drawer.
     * @param {HTMLElement | null} trigger
     * @returns {boolean}
     */
    isMobileContext(trigger) {
      return Boolean(trigger?.closest('[data-header-v2-drawer]'));
    }

    /**
     * Opens matching content when a merchant selects a Theme Block.
     * @param {CustomEvent} event
     */
    handleBlockSelect(event) {
      const panel = event.target?.closest?.('[data-header-v2-panel]');
      if (!panel) return;

      const title = normalizeMenuTitle(panel.dataset.menuTitle);
      const trigger = Array.from(this.querySelectorAll('[data-header-v2-trigger][data-menu-level="primary"]')).find(
        (candidate) => normalizeMenuTitle(candidate.dataset.menuTitle) === title,
      );

      if (trigger) this.openSpecialPanel(trigger, panel, this.desktopMedia.matches ? 'desktop' : 'mobile');
    }

    /**
     * Enables transparent header only when the merchant switch is on and the
     * first page section opts in. Theme is read from data-header-theme on that
     * section so Header does not depend on Slideshow internals.
     */
    setupTransparency() {
      this.themeObserver?.disconnect();

      const optIn = document.querySelector('.shopify-section:first-child [allow-transparent-header]');
      if (!this.hasAttribute('data-enable-transparent') || !optIn) {
        this.removeAttribute('allow-transparency');
        this.removeAttribute('data-transparent-theme');
        this.syncHeaderFill();
        return;
      }

      this.setAttribute('allow-transparency', '');
      this.syncTransparentTheme(optIn);
      this.themeObserver = new MutationObserver(() => this.syncTransparentTheme(optIn));
      this.themeObserver.observe(optIn, { attributes: true, attributeFilter: ['data-header-theme'] });
      this.syncHeaderFill();
    }

    /**
     * Copies the first section's published theme onto the header.
     * @param {Element} optIn
     */
    syncTransparentTheme(optIn) {
      const theme = optIn.getAttribute('data-header-theme') === 'dark' ? 'dark' : 'light';
      this.setAttribute('data-transparent-theme', theme);
    }

    /**
     * Synchronizes the filled state with scrolling, open menus, and PC nav hover.
     */
    syncHeaderFill() {
      if (!this.hasAttribute('allow-transparency')) {
        this.classList.remove('is-filled');
        return;
      }

      const isFilled =
        window.scrollY > 20 ||
        Boolean(this.activePanel) ||
        this.classList.contains('is-mobile-menu-open') ||
        (this.desktopMedia.matches && this.isDesktopNavHovered);
      this.classList.toggle('is-filled', isFilled);
    }

    /**
     * Preserves the legacy accumulated-scroll hide and upward-scroll reveal behavior, except that a hidden header
     * stays hidden on upward scroll while a product page anchor bar is pinned (the bar then serves as the navigation;
     * user request 2026-10-29). Once the bar is back in the page flow, upward scroll reveals the header as before.
     */
    handleHeaderScroll() {
      if (this.classList.contains('is-mobile-menu-open')) {
        this.lastScrollTop = Math.max(0, window.scrollY);
        return;
      }

      const currentScrollTop = Math.max(0, window.scrollY);
      const delta = currentScrollTop - this.lastScrollTop;

      if (delta < 0) {
        this.accumulatedScroll = 0;
        if (this.isHeaderVisible || !this.isProductAnchorBarPinned()) this.showHeader();
      } else {
        this.accumulatedScroll = Math.max(0, this.accumulatedScroll + delta);
        if (this.accumulatedScroll > Number(this.getAttribute('hide-on-scroll') || 100)) {
          this.hideHeader();
        }
      }

      this.lastScrollTop = currentScrollTop;
    }

    /**
     * Whether a product page anchor bar (<product-anchor-tabs>, sections/anchor_tab.liquid) is visible and pinned:
     * its sticky section sits at its sticky `top` instead of its place in the page flow. Global coordination with
     * the product page, so the lookup is document-wide.
     * @returns {boolean}
     */
    isProductAnchorBarPinned() {
      for (const bar of document.querySelectorAll('product-anchor-tabs')) {
        const rect = bar.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0 || getComputedStyle(bar).visibility === 'hidden') continue;
        const section = bar.closest('.shopify-section') ?? bar;
        const stickyTop = Number.parseFloat(getComputedStyle(section).top);
        if (Number.isFinite(stickyTop) && section.getBoundingClientRect().top <= stickyTop + 1) return true;
      }
      return false;
    }

    /**
     * The sticky box is the header section, not this element: moving only the inner header would leave an empty
     * band at the top of the viewport that still catches clicks.
     * @returns {HTMLElement}
     */
    get stickyTarget() {
      return this.sectionElement ?? this.closest('.shopify-section') ?? this;
    }

    hideHeader() {
      if (!this.isHeaderVisible || this.activePanel || this.classList.contains('is-mobile-menu-open')) {
        return;
      }
      this.stickyTarget.style.transform = 'translateY(-100%)';
      this.isHeaderVisible = false;
      this.syncHeaderVisibility();
    }

    showHeader() {
      // Cleared rather than set to translateY(0): any transform makes the section the containing block of the fixed
      // Pad/MO menu overlay.
      this.stickyTarget.style.removeProperty('transform');
      this.accumulatedScroll = 0;
      if (this.isHeaderVisible) return;
      this.isHeaderVisible = true;
      this.syncHeaderVisibility();
    }

    /**
     * Publishes the header state with the same contract as <store-header> (assets/sections.js.liquid), so sticky
     * elements below it (anchor tabs, product media, compare bars) move up into the freed space: --header-is-visible
     * on :root, and `header:visibility-change` on document with `detail: { visible: boolean }`.
     */
    syncHeaderVisibility() {
      document.documentElement.style.setProperty('--header-is-visible', this.isHeaderVisible ? '1' : '0');
      document.dispatchEvent(new CustomEvent('header:visibility-change', { detail: { visible: this.isHeaderVisible } }));
    }
  }

  if (!customElements.get('store-header-v2')) {
    customElements.define('store-header-v2', StoreHeaderV2);
  }
}
